import { cn } from '@/lib/cn'
import { Plus, Search, X } from 'lucide-react'
import { useEffect, useId, useMemo, useRef, useState, type ReactNode } from 'react'

/** The default fold: the value is stored as it was typed. */
const AS_TYPED = (value: string) => value

export interface TokenSelectProps {
  label?: ReactNode
  values: string[]
  /** The vocabulary. Everything the user may choose comes from here. */
  options: string[]
  onChange: (values: string[]) => void
  /**
   * How a typed value becomes the value that is stored. Tags are folded, so
   * "Instant Pot" and "instant_pot" are one tag; cuisines are left alone.
   */
  normalize?: (value: string) => string
  /** Whether a value that is not among `options` may be added. */
  allowCustom?: boolean
  /** What to say when the typed text matches nothing in `options`. */
  unknownMessage?: (term: string) => string
  searchPlaceholder?: string
  hint?: ReactNode
  warning?: ReactNode
}

/**
 * Several values picked from a vocabulary, with a search box.
 *
 * The chosen values are chips, so they can be taken off one at a time, and the
 * list below the box is filtered by what is typed. The list is the whole
 * vocabulary: it is never narrowed by the other rules in force, because
 * removing a rule widens what is available and the picker would then be
 * showing a stale set.
 */
export function TokenSelect({
  label,
  values,
  options,
  onChange,
  normalize,
  allowCustom = false,
  unknownMessage,
  searchPlaceholder = 'Search…',
  hint,
  warning,
}: TokenSelectProps) {
  const [term, setTerm] = useState('')
  const [open, setOpen] = useState(false)
  const [highlighted, setHighlighted] = useState(0)
  const container = useRef<HTMLDivElement>(null)
  const listId = useId()

  const fold = normalize ?? AS_TYPED
  const needle = term.trim()

  const entries = useMemo(() => {
    const wanted = fold(needle).toLowerCase()
    const matches = options.filter((option) =>
      fold(option).toLowerCase().includes(wanted),
    )
    const available = matches.filter((option) => !values.includes(option))
    // A value the collection does not have yet: only tags may be one.
    const custom =
      allowCustom &&
      needle !== '' &&
      !options.some((option) => fold(option).toLowerCase() === wanted)
    return {
      matches,
      list: [
        ...available.map((value) => ({ value, custom: false })),
        ...(custom ? [{ value: fold(needle), custom: true }] : []),
      ],
    }
  }, [options, values, needle, fold, allowCustom])

  useEffect(() => {
    if (!open) return
    const onClick = (event: MouseEvent) => {
      if (!container.current?.contains(event.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onClick)
    return () => document.removeEventListener('mousedown', onClick)
  }, [open])

  function add(value: string) {
    const stored = fold(value)
    if (values.includes(stored)) return
    onChange([...values, stored])
    setTerm('')
    setHighlighted(0)
  }

  function remove(value: string) {
    onChange(values.filter((entry) => entry !== value))
  }

  const active = entries.list[Math.min(highlighted, entries.list.length - 1)]

  return (
    <div className="flex min-w-0 flex-col gap-1.5">
      {label ? <span className="text-label font-medium text-ink-soft">{label}</span> : null}

      {values.length > 0 ? (
        <ul className="flex flex-wrap gap-1.5">
          {values.map((value) => (
            <li
              key={value}
              className="flex items-center gap-0.5 rounded-full bg-surface-sunken py-0.5 pr-1 pl-2 text-micro text-ink-soft ring-1 ring-line"
            >
              <span className="max-w-40 truncate">{value}</span>
              <button
                type="button"
                onClick={() => remove(value)}
                aria-label={`Remove ${value}`}
                className="rounded-full p-0.5 text-ink-faint hover:bg-surface hover:text-ink"
              >
                <X size={12} aria-hidden />
              </button>
            </li>
          ))}
        </ul>
      ) : null}

      <div ref={container} className="relative">
        <Search
          size={15}
          aria-hidden
          className="pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-ink-faint"
        />
        <input
          type="text"
          role="combobox"
          aria-expanded={open}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-label={typeof label === 'string' ? `${label}: search` : 'Search'}
          placeholder={searchPlaceholder}
          value={term}
          onFocus={() => setOpen(true)}
          onChange={(event) => {
            setTerm(event.target.value)
            setHighlighted(0)
            setOpen(true)
          }}
          onKeyDown={(event) => {
            if (event.key === 'ArrowDown') {
              event.preventDefault()
              setHighlighted((current) => Math.min(current + 1, entries.list.length - 1))
            } else if (event.key === 'ArrowUp') {
              event.preventDefault()
              setHighlighted((current) => Math.max(current - 1, 0))
            } else if (event.key === 'Enter') {
              event.preventDefault()
              if (active) add(active.value)
            } else if (event.key === 'Escape') {
              setOpen(false)
            }
          }}
          className="h-10 w-full rounded-[var(--radius-field)] border border-line-strong bg-surface pr-3 pl-9 text-sm text-ink placeholder:text-ink-faint/70 hover:border-ink-faint focus:border-accent"
        />

        {open ? (
          <div className="absolute top-full left-0 z-20 mt-1 w-full min-w-48 rounded-[var(--radius-field)] border border-line bg-surface p-1.5 shadow-raised">
            {needle && entries.matches.length === 0 ? (
              <p className="px-2 py-1.5 text-micro text-pretty text-caution">
                {unknownMessage ? unknownMessage(needle) : `Nothing matches “${needle}”.`}
              </p>
            ) : null}

            {entries.list.length > 0 ? (
              <ul id={listId} role="listbox" className="scroll-slim max-h-56 overflow-y-auto">
                {entries.list.map((entry, index) => (
                  <li key={entry.value}>
                    <button
                      type="button"
                      role="option"
                      aria-selected={false}
                      onMouseEnter={() => setHighlighted(index)}
                      onClick={() => add(entry.value)}
                      className={cn(
                        'flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm',
                        index === highlighted
                          ? 'bg-accent-soft text-accent-ink'
                          : 'text-ink hover:bg-surface-sunken',
                      )}
                    >
                      {entry.custom ? <Plus size={13} aria-hidden /> : null}
                      <span className="truncate">
                        {entry.custom ? `Add “${entry.value}”` : entry.value}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            ) : null}

            {entries.list.length === 0 ? (
              <p className="px-2 py-3 text-center text-xs text-ink-faint">
                {needle ? 'Already chosen.' : 'Nothing left to add.'}
              </p>
            ) : null}
          </div>
        ) : null}
      </div>

      {warning ? (
        <p className="text-micro text-pretty text-caution">{warning}</p>
      ) : hint ? (
        <p className="text-micro text-pretty text-ink-faint">{hint}</p>
      ) : null}
    </div>
  )
}
